import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import { API_URL } from '../../config/constants';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Import componente tab-uri
import ProfileTab from './components/ProfileTab';
import FavoriteBooksTab from './components/FavoriteBooksTab';
import NotesTab from './components/NotesTab';

interface Collection {
  id: number;
  name: string;
  description: string;
  bookCount: number;
}

interface Note {
  id: number;
  bookId: number;
  bookTitle: string;
  content: string;
  createdAt: string;
}

interface UserData {
  id: number;
  name: string;
  email: string;
  role: string;
  createdAt: string;
}

const UserProfile: React.FC = () => {
  const { user, token, isAuthenticated, logout } = useAuth();
  const { favorites } = useFavorites();
  const navigate = useNavigate();
  
  // State pentru datele utilizatorului
  const [userData, setUserData] = useState<UserData | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [userStats, setUserStats] = useState({
    totalFavorites: 0,
    totalCollections: 0,
    totalNotes: 0,
    memberSince: ''
  });
  
  // State pentru mesaje
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  
  // State pentru loading și tab activ
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Navigare către login dacă utilizatorul nu este autentificat
  useEffect(() => {
    if (!isAuthenticated || !token) {
      navigate('/login');
    } else {
      // Inițializăm datele de profil
      fetchUserProfile();
      fetchUserCollections();
      fetchUserNotes();
    }
  }, [isAuthenticated, token, navigate]);

  // Actualizează statisticile când se schimbă favoritele
  useEffect(() => {
    setUserStats(prev => ({
      ...prev,
      totalFavorites: favorites.length
    }));
  }, [favorites]);

  // Funcție pentru afișarea mesajului de succes pentru o perioadă limitată de timp
  const showMessage = (message: string, type: 'success' | 'error') => {
    setMessage(message);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
    }, 3000);
  };
  
  // Fetch pentru notițele utilizatorului
  const fetchUserNotes = async () => {
    try {
      const response = await fetch(`${API_URL}/annotations/my-notes`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Eroare la obținerea notițelor');
      }
      
      const notes = await response.json();
      
      // Actualizăm statisticile cu numărul real de notițe
      setUserStats(prev => ({
        ...prev,
        totalNotes: notes.length || 0
      }));
    } catch (error) {
      console.error('Eroare la obținerea notițelor:', error);
      // Nu afișăm eroare pentru notițe, doar setăm la 0
      setUserStats(prev => ({
        ...prev,
        totalNotes: 0
      }));
    }
  };
  
  // Fetch pentru profilul utilizatorului
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${API_URL}/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Eroare la obținerea profilului');
      }
      
      const data = await response.json();
      setUserData(data.user);
      
      // Actualizăm data de membru din datele utilizatorului
      if (data.user.createdAt) {
        setUserStats(prev => ({
          ...prev,
          memberSince: new Date(data.user.createdAt).getFullYear().toString()
        }));
      }
    } catch (error) {
      console.error('Eroare la obținerea profilului:', error);
      showMessage('Nu s-a putut încărca profilul utilizatorului', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch pentru colecțiile utilizatorului
  const fetchUserCollections = async () => {
    try {
      const response = await fetch(`${API_URL}/collections/user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Eroare la obținerea colecțiilor');
      }
      
      const data = await response.json();
      setCollections(data.collections || []);
      
      // Actualizăm statisticile
      setUserStats(prev => ({
        ...prev,
        totalCollections: data.collections?.length || 0
      }));
    } catch (error) {
      console.error('Eroare la obținerea colecțiilor:', error);
    }
  };
  
  // Actualizare profil utilizator
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const newName = formData.get('name') as string;
    const newEmail = formData.get('email') as string;
    
    if (!newName.trim() || !newEmail.trim()) {
      showMessage('Toate câmpurile sunt obligatorii', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/users/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newName,
          email: newEmail
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la actualizarea profilului');
      }
      
      // Actualizăm datele utilizatorului în localStorage
      const updatedUser = {
        ...user,
        name: newName,
        email: newEmail
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      showMessage('Profilul a fost actualizat cu succes', 'success');
      fetchUserProfile();
    } catch (error) {
      console.error('Eroare la actualizarea profilului:', error);
      showMessage(error instanceof Error ? error.message : 'Eroare la actualizarea profilului', 'error');
    }
  };
  
  // Schimbare parolă utilizator
  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const currentPassword = formData.get('currentPassword') as string;
    const newPassword = formData.get('newPassword') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      showMessage('Toate câmpurile sunt obligatorii', 'error');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      showMessage('Parolele noi nu se potrivesc', 'error');
      return;
    }
    
    if (newPassword.length < 6) {
      showMessage('Parola trebuie să aibă cel puțin 6 caractere', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la schimbarea parolei');
      }
      
      showMessage('Parola a fost schimbată cu succes', 'success');
    } catch (error) {
      console.error('Eroare la schimbarea parolei:', error);
      showMessage(error instanceof Error ? error.message : 'Eroare la schimbarea parolei', 'error');
    }
  };
  
  // Tab-urile actualizate
  const tabs = [
    { id: 'profile', name: 'Profil', icon: 'user' },
    { id: 'favorites', name: 'Cărți Favorite', icon: 'heart' },
    { id: 'collections', name: 'Colecții', icon: 'collection' },
    { id: 'notes', name: 'Notițe', icon: 'document' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileTab 
            userData={userData}
            handleUpdateProfile={handleUpdateProfile}
            handleChangePassword={handleChangePassword}
          />
        );
      case 'favorites':
        return <FavoriteBooksTab />;
      case 'notes':
        return <NotesTab />;
      case 'collections':
        return (
          <div>
            <h2 className="text-lg font-medium text-gray-900 mb-4">Colecțiile Mele</h2>
            {collections.length === 0 ? (
              <div className="text-center py-8">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Nu ai nicio colecție</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Colecțiile îți permit să organizezi cărțile după preferințe.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {collections.map((collection) => (
                  <div key={collection.id} className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{collection.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{collection.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        {collection.bookCount} {collection.bookCount === 1 ? 'carte' : 'cărți'}
                      </span>
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        Vezi colecția
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="bg-white shadow rounded-lg p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
          {/* Header profil */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Profilul meu</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Administrează-ți contul și vezi activitatea
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={() => logout()}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm5 4a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                  Deconectare
                </button>
              </div>
            </div>
          </div>
          
          {/* Mesaj de succes sau eroare */}
          {message && (
            <div className={`mb-6 ${messageType === 'success' ? 'bg-green-100 border border-green-400 text-green-700' : 'bg-red-100 border border-red-400 text-red-700'} px-4 py-3 rounded relative`} role="alert">
              <span className="block sm:inline">{message}</span>
            </div>
          )}
          
          {/* Statistici utilizator actualizate */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Cărți Favorite</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.totalFavorites}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Colecții Personale</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.totalCollections}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Note și Semne de Carte</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{userStats.totalNotes}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Membru din</dt>
                <dd className="mt-1 text-lg font-semibold text-gray-900">
                  {userStats.memberSince}
                </dd>
              </div>
            </div>
          </div>
          
          {/* Tab-uri de navigare */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="p-6">
              {renderTabContent()}
            </div>
          </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UserProfile; 
